import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import { FormEvent, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { If } from '../../../common/IfComponent';
import {
    CostDto,
    CostGroupDto,
    CostType,
    CostWeightType,
    CreateCostRequest,
    PalletType,
    ProductCategoryDto,
    ProductDto,
    ProductGroupDto
} from '../../../common/PricePrgClient';
import { RootState } from '../../../store';
import { getProductCategories, getProductGroups, getProducts } from '../../product/redux/productSelectors';
import { createCost, showEditCostDialog, updateCost } from '../redux/calculationSlices';
import PercentageCostDialogContent from './PercentageCostDialogContent';
import PricePerPackageCostDialogContent from './PricePerPackageCostDialogContent';
import PricePerPalletCostDialogContent from './PricePerPalletCostDialogContent';
import PricePerPriceUnitCostDialogContent from './PricePerPriceUnitCostDialogContent';
import ProductCategoryCostDialogContent from './ProductCategoryCostDialogContent';
import ProductCostDialogContent from './ProductCostDialogContent';
import ProductGroupCostDialogContent from './ProductGroupCostDialogContent';

const mapStateToProps = (state: RootState) => ({
    isEditingCost: state.calculation.isEditingCost,
    isEditCostDialogVisible: state.calculation.isEditCostDialogVisible,
    isEditCostDialogLoading: state.calculation.isEditCostDialogLoading,
    calculation: state.calculation.calculation,
    products: getProducts(state),
    productCategories: getProductCategories(state),
    productGroups: getProductGroups(state)
});

type Props = ReturnType<typeof mapStateToProps>;

const EditCostDialog = ({
    isEditingCost,
    isEditCostDialogVisible,
    isEditCostDialogLoading,
    calculation,
    productCategories,
    productGroups,
    products
}: Props) => {
    const dispatch = useDispatch();
    const dispatchHideNewCostDialog = () => {
        dispatch(showEditCostDialog(null));
    };

    const getInitialFormState = () => ({
        name: isEditingCost?.name ?? '',
        amount: isEditingCost?.amount ?? 0,
        useBasePrice: isEditingCost?.useBasePrice ?? false,
        selectedProductCategoryId: isEditingCost?.productCategoryId ?? 0,
        selectedProductCategoryName:
            isEditingCost?.productCategoryId != null
                ? productCategories[isEditingCost.productCategoryId]?.name ?? ''
                : '',
        selectedProductGroupId: isEditingCost?.productGroupId ?? 0,
        selectedProductGroupName:
            isEditingCost?.productGroupId != null
                ? productGroups[isEditingCost.productGroupId]?.name ?? ''
                : '',
        selectedProductId: isEditingCost?.productId ?? 0,
        selectedProductName:
            isEditingCost?.productId != null ? products[isEditingCost.productId]?.name ?? '' : '',
        costWeightType:
            isEditingCost?.costWeightType ?? (CostWeightType.GrossWeight as CostWeightType | undefined),
        palletType: isEditingCost?.palletType ?? (PalletType.EUR as PalletType | undefined)
    });

    const [formState, setState] = useState(getInitialFormState());

    useEffect(() => {
        setState({
            name: isEditingCost?.name ?? '',
            amount: isEditingCost?.amount ?? 0,
            useBasePrice: isEditingCost?.useBasePrice ?? false,
            selectedProductCategoryId: isEditingCost?.productCategoryId ?? 0,
            selectedProductCategoryName:
                isEditingCost?.productCategoryId != null
                    ? productCategories[isEditingCost.productCategoryId]?.name ?? ''
                    : '',
            selectedProductGroupId: isEditingCost?.productGroupId ?? 0,
            selectedProductGroupName:
                isEditingCost?.productGroupId != null
                    ? productGroups[isEditingCost.productGroupId]?.name ?? ''
                    : '',
            selectedProductId: isEditingCost?.productId ?? 0,
            selectedProductName:
                isEditingCost?.productId != null ? products[isEditingCost.productId]?.name ?? '' : '',
            costWeightType: isEditingCost?.costWeightType,
            palletType: isEditingCost?.palletType ?? PalletType.EUR
        });
    }, [isEditingCost, products, productCategories, productGroups]);

    const handleUseBasePrice = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState({ ...formState, [event.target.name]: event.target.checked });
    };

    const handleNameChange = (inputValue: string) => {
        setState({ ...formState, name: inputValue });
    };

    const handleAmountChange = (inputValue: number) => {
        setState({ ...formState, amount: inputValue });
    };
    const handleCostWeightTypeChange = (inputValue: CostWeightType) => {
        setState({ ...formState, costWeightType: inputValue });
    };
    const handleProductCategorySelected = (productCategory: ProductCategoryDto | null) => {
        setState({
            ...formState,
            selectedProductGroupId: productCategory?.productCategoryId ?? 0,
            selectedProductCategoryName: productCategory?.name ?? '',
            name: productCategory?.name ?? ''
        });
    };

    const handleProductGroupSelected = (productGroup: ProductGroupDto | null) => {
        setState({
            ...formState,
            selectedProductGroupId: productGroup?.productGroupId ?? 0,
            selectedProductGroupName: productGroup?.name ?? '',
            name: productGroup?.name ?? ''
        });
    };

    const handleProductSelected = (product: ProductDto | null) => {
        setState({
            ...formState,
            selectedProductId: product?.productId ?? 0,
            selectedProductName: product?.name ?? '',
            name: product?.name ?? ''
        });
    };

    const handlePalletTypeChange = (inputValue: PalletType) => {
        setState({ ...formState, palletType: inputValue });
    };

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (shouldCreateCost()) {
            dispatchCreateCost();
        } else {
            dispatchUpdateCost();
        }
    };

    const getCostGroupId = (cost: CostDto) => {
        let overridenCostGroup: CostGroupDto | undefined;
        calculation.costGroups.forEach(c => {
            c.costs.forEach(d => {
                if (d.costId === cost.costId) {
                    overridenCostGroup = c;
                }
            });
        });

        return overridenCostGroup?.costGroupId ?? cost.costGroupId;
    };

    const getCostName = (costType: CostType | undefined) => {
        switch (costType) {
            case CostType.ProductCategoryPercentage:
                return formState.selectedProductCategoryName;
            case CostType.ProductPriceUnit:
            case CostType.ProductPercentage:
                return formState.selectedProductName;
            case CostType.ProductGroupPercentage:
                return formState.selectedProductGroupName;
        }
        return formState.name;
    };

    const dispatchCreateCost = () => {
        const request: CreateCostRequest = {
            name: getCostName(isEditingCost?.costType),
            useBasePrice: formState.useBasePrice,
            amount: formState.amount,
            costGroupId: getCostGroupId(isEditingCost!),
            costType: isEditingCost?.costType ?? 0,
            order: isEditingCost?.order ?? 0,
            overridesCostId: isEditingCost?.costId,
            productCategoryId: formState.selectedProductCategoryId,
            productGroupId: formState.selectedProductGroupId,
            productId: formState.selectedProductId,
            costWeightType: formState.costWeightType,
            palletType: formState.palletType
        };

        dispatch(
            createCost.request({
                costGroupId: request.costGroupId,
                createCostRequest: request
            })
        );
    };

    const dispatchUpdateCost = () => {
        const costDto: CostDto = {
            costId: isEditingCost?.costId ?? 0,
            name: getCostName(isEditingCost?.costType),
            useBasePrice: formState.useBasePrice,
            amount: formState.amount,
            costGroupId: isEditingCost?.costGroupId ?? 0,
            costType: isEditingCost?.costType ?? 0,
            order: isEditingCost?.order ?? 0,
            overridesCostId:
                isEditingCost?.inheritedFrom != null ? isEditingCost.costId : isEditingCost?.overridesCostId,
            productCategoryId: formState.selectedProductCategoryId,
            productGroupId: formState.selectedProductGroupId,
            productId: formState.selectedProductId,
            costWeightType: formState.costWeightType,
            palletType: formState.palletType
        };

        dispatch(updateCost.request(costDto));
    };

    const shouldCreateCost = () => {
        if (isEditingCost?.inheritedFrom != null) {
            return true;
        } else if (isEditingCost?.overridesCostId == null && isEditingCost?.costId === 0) {
            return true;
        }
        return false;
    };

    const getDialogContent = () => {
        if (isEditingCost != null) {
            switch (isEditingCost!.costType as CostType) {
                case CostType.Percentage:
                    return (
                        <PercentageCostDialogContent
                            handleAmountChange={handleAmountChange}
                            useBasePrice={formState.useBasePrice}
                            handleUseBasePrice={handleUseBasePrice}
                            handleNameChange={handleNameChange}
                            name={formState.name}
                            amount={formState.amount}
                        />
                    );
                case CostType.PriceUnit:
                    return (
                        <PricePerPriceUnitCostDialogContent
                            handleAmountChange={handleAmountChange}
                            handleNameChange={handleNameChange}
                            name={formState.name}
                            amount={formState.amount}
                            costWeightType={formState.costWeightType}
                            handleCostWeightTypeChanged={handleCostWeightTypeChange}
                        />
                    );
                case CostType.PerPallet:
                    return (
                        <PricePerPalletCostDialogContent
                            handleAmountChange={handleAmountChange}
                            handleNameChange={handleNameChange}
                            handlePalletTypeChange={handlePalletTypeChange}
                            name={formState.name}
                            amount={formState.amount}
                            palletType={formState.palletType}
                        />
                    );
                case CostType.PerBox:
                    return (
                        <PricePerPackageCostDialogContent
                            handleAmountChange={handleAmountChange}
                            handleNameChange={handleNameChange}
                            handleUseBasePrice={handleUseBasePrice}
                            useBasePrice={formState.useBasePrice}
                            name={formState.name}
                            amount={formState.amount}
                        />
                    );
                case CostType.ProductCategoryPercentage:
                    return (
                        <ProductCategoryCostDialogContent
                            handleAmountChange={handleAmountChange}
                            handleProductCategorySelected={handleProductCategorySelected}
                            handleUseBasePrice={handleUseBasePrice}
                            useBasePrice={formState.useBasePrice}
                            name={formState.name}
                            amount={formState.amount}
                            productCategoryId={formState.selectedProductCategoryId}
                        />
                    );
                case CostType.ProductGroupPercentage:
                    return (
                        <ProductGroupCostDialogContent
                            handleAmountChange={handleAmountChange}
                            amount={formState.amount}
                            handleProductGroupChanged={handleProductGroupSelected}
                            handleUseBasePrice={handleUseBasePrice}
                            productGroupId={formState.selectedProductGroupId}
                            useBasePrice={formState.useBasePrice}
                        />
                    );
                case CostType.ProductPercentage:
                case CostType.ProductPriceUnit:
                    return (
                        <ProductCostDialogContent
                            amount={formState.amount}
                            handleUseBasePrice={handleUseBasePrice}
                            productId={formState.selectedProductId}
                            useBasePrice={formState.useBasePrice}
                            handleAmountChange={handleAmountChange}
                            handleProductChanged={handleProductSelected}
                            handleCostWeightTypeChanged={handleCostWeightTypeChange}
                            costType={isEditingCost.costType}
                            costWeightType={formState.costWeightType}
                        />
                    );
            }
        }
        return null;
    };

    const getTitle = () => {
        if (isEditingCost?.inheritedFrom != null) {
            return 'Override cost';
        }
        return 'Edit cost';
    };

    return (
        <Dialog
            onClose={dispatchHideNewCostDialog}
            aria-labelledby="simple-dialog-title"
            open={isEditCostDialogVisible}
            // Todo Check if Paper needs this prop instead
            sx={{ minHeight: '80%' }}
            maxWidth="sm"
            fullWidth={true}
        >
            <DialogTitle id="simple-dialog-title">{getTitle()}</DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    {getDialogContent()}
                    <If condition={isEditCostDialogLoading} then={<LinearProgress />} else={null} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={dispatchHideNewCostDialog} color="primary">
                        Close
                    </Button>
                    <Button onClick={handleSubmit} type="submit" color="primary">
                        Save
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default connect(mapStateToProps)(EditCostDialog);
